// frontend/src/components/StructuredSurgicalHistoryForm.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Spin, message, Card, Typography, Checkbox, Alert } from 'antd';
import surgicalHistoryProcedures from '../utils/surgicalHistoryProcedures'; // Import the new procedures list

const { Title } = Typography;

// Helper to create Checkbox.Group options
const createCheckboxOptions = (procedures) => {
    return procedures.map(proc => ({ label: proc, value: proc }));
};

// Helper to render category titles
const formatCategoryName = (categoryKey) => {
    if (!categoryKey) return '';
    // Add Vietnamese titles for the new categories
    if (categoryKey === 'general') return 'TỔNG QUÁT';
    if (categoryKey === 'neuro_ent_eye') return 'THẦN KINH - TAI MŨI HỌNG - MẮT';
    if (categoryKey === 'cardiac_thoracic') return 'TIM MẠCH - LỒNG NGỰC';
    if (categoryKey === 'gi_hepatobiliary') return 'TIÊU HÓA - GAN MẬT';
    if (categoryKey === 'renal_urology') return 'THẬN - TIẾT NIỆU';
    if (categoryKey === 'orthopedic') return 'CƠ XƯƠNG KHỚP';
    if (categoryKey === 'gynecology_breast') return 'PHỤ KHOA - VÚ';

    // Exclude internal fields
    if (['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(categoryKey)) return null;

    // Default formatting for 'other' or any future keys
    return categoryKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const StructuredSurgicalHistoryForm = ({ patientId, onSaveSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!patientId) return;
        setFetching(true);
        setFetchError(null);
        try {
            const response = await axios.get(`/patients/${patientId}`);
            const patientData = response.data.data.patient;
            // Fetch structured SURGICAL history
            const currentHistory = patientData.structuredSurgicalHistory || {};

            console.log("Fetched structured surgical history:", currentHistory);

            // Validate that we have the expected structure
            if (currentHistory && typeof currentHistory === 'object') {
                console.log("✅ Valid structured surgical history data found");
            } else {
                console.warn("⚠️ No structured surgical history data found, using empty structure");
            }

            // Fix: Set the form values with the correct nested structure
            // The form expects { structuredSurgicalHistory: { systemKey: [values] } }
            form.setFieldsValue({
                structuredSurgicalHistory: {
                    general: currentHistory.general || [],
                    neuro_ent_eye: currentHistory.neuro_ent_eye || [],
                    cardiac_thoracic: currentHistory.cardiac_thoracic || [],
                    gi_hepatobiliary: currentHistory.gi_hepatobiliary || [],
                    renal_urology: currentHistory.renal_urology || [],
                    orthopedic: currentHistory.orthopedic || [],
                    gynecology_breast: currentHistory.gynecology_breast || [],
                    other: currentHistory.other || []
                }
            });

        } catch (err) {
            console.error("Error fetching structured surgical history:", err);
            setFetchError(err.response?.data?.message || "Failed to load current surgical history.");
            // Reset form with empty arrays for each system
            form.setFieldsValue({
                structuredSurgicalHistory: {
                    general: [],
                    neuro_ent_eye: [],
                    cardiac_thoracic: [],
                    gi_hepatobiliary: [],
                    renal_urology: [],
                    orthopedic: [],
                    gynecology_breast: [],
                    other: []
                }
            });
        } finally {
            setFetching(false);
        }
    }, [patientId, form]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const handleFinish = async (values) => {
        if (!patientId) {
            message.error("Patient ID is missing.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                 // Send only the structuredSurgicalHistory field
                structuredSurgicalHistory: values.structuredSurgicalHistory || {}
            };

            await axios.patch(`/patients/${patientId}`, payload);

            message.success('Surgical history checklist updated successfully!');
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } catch (err) {
            console.error("Error saving structured surgical history:", err);
            message.error(err.response?.data?.message || 'Failed to save surgical history checklist.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading || fetching}>
             {fetchError && <Alert message={fetchError} type="error" showIcon style={{ marginBottom: 16 }} />}
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Card size="small" title="Structured Surgical History Checklist">
                    {Object.entries(surgicalHistoryProcedures)
                        .filter(([key, value]) => key !== 'other' || value.length > 0)
                        .map(([categoryKey, procedures]) => {
                             const categoryName = formatCategoryName(categoryKey);
                             return categoryName ? (
                                <Form.Item
                                    key={categoryKey}
                                    name={['structuredSurgicalHistory', categoryKey]} // Use correct top-level name
                                    label={<Title level={5} style={{ marginTop: '10px', marginBottom: '5px' }}>{categoryName}</Title>}
                                    style={{ marginBottom: 10 }}
                                >
                                    <Checkbox.Group options={createCheckboxOptions(procedures)} />
                                </Form.Item>
                             ) : null;
                        })}
                </Card>

                <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
                    <Button style={{ marginRight: 8 }} onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Save Checklist
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default StructuredSurgicalHistoryForm;
